import { useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { RedemptionsTable, type RedemptionFilters } from '../components/RedemptionsTable';
import { REDEMPTION_STATUSES } from '../lib/triggerCodes';
import { useTriggerOptions } from '../lib/useTriggerOptions';

const input = 'rounded border-2 border-ink bg-white px-2 py-1';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A `date` input (local day) as the ISO instant of that day's start, or the next day's start for an inclusive "to". */
function dayStart(day: string, next = false) {
  if (!day) return undefined;
  const d = new Date(`${day}T00:00`);
  if (next) d.setDate(d.getDate() + 1);
  return d.toISOString();
}

/**
 * Trigger redemptions (ADR 0029 §12): every accepted redeem, searchable by player, trigger, code, status and date, with
 * the stored result, and an Admin-only void.
 */
export function TriggerRedemptionsPage() {
  const triggers = useTriggerOptions();
  const [form, setForm] = useState({
    playerId: '',
    triggerId: '',
    code: '',
    status: '',
    from: '',
    to: '',
  });
  const playerOk = !form.playerId.trim() || UUID.test(form.playerId.trim());
  const filters: RedemptionFilters = {
    playerId: playerOk ? form.playerId.trim() : undefined,
    triggerId: form.triggerId,
    code: form.code.trim(),
    status: form.status,
    from: dayStart(form.from),
    to: dayStart(form.to, true),
  };

  return (
    <>
      <PageHeader
        title="Redemptions"
        description="Every accepted scan-trigger redeem: who got what, from which code, and the exact stored result. Void (Admin) stops it counting toward once and limits without taking anything back."
      />
      <div className="mb-4 flex flex-wrap items-end gap-2 text-sm">
        <input
          aria-label="Player id"
          placeholder="player id (from the Players page)"
          value={form.playerId}
          onChange={(e) => setForm({ ...form, playerId: e.target.value })}
          aria-invalid={!playerOk}
          className={`${input} w-80 font-mono aria-[invalid=true]:border-blam`}
        />
        <select
          aria-label="Trigger"
          value={form.triggerId}
          onChange={(e) => setForm({ ...form, triggerId: e.target.value })}
          className={input}
        >
          <option value="">any trigger</option>
          {triggers.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <input
          aria-label="Code"
          placeholder="code contains…"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
          className={`${input} w-44 font-mono`}
        />
        <select
          aria-label="Status"
          value={form.status}
          onChange={(e) => setForm({ ...form, status: e.target.value })}
          className={input}
        >
          <option value="">any status</option>
          {REDEMPTION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <label className="flex flex-col text-xs font-semibold">
          From
          <input
            aria-label="From"
            type="date"
            value={form.from}
            onChange={(e) => setForm({ ...form, from: e.target.value })}
            className={`${input} font-normal`}
          />
        </label>
        <label className="flex flex-col text-xs font-semibold">
          To (inclusive)
          <input
            aria-label="To"
            type="date"
            value={form.to}
            onChange={(e) => setForm({ ...form, to: e.target.value })}
            className={`${input} font-normal`}
          />
        </label>
      </div>
      {!playerOk && (
        <p className="mb-2 text-xs text-blam" role="alert">
          A player id is a UUID (open the player from the Players page).
        </p>
      )}
      <RedemptionsTable filters={filters} />
    </>
  );
}
